import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Modal from "./Modal";
import { adminApiRequest, ADMIN_PROXY_URL } from "../utils/adminApi";

// ─────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────
const ACCEPTED_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "image/png",
  "image/jpeg",
  "image/webp",
  "text/plain",
];

const ACCEPT_STRING = ACCEPTED_TYPES.join(",");
const MAX_FILE_MB = 20;
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
function fileIcon(mimeOrName) {
  const m = (mimeOrName || "").toLowerCase();
  if (m.includes("pdf")) return { icon: "fa-solid fa-file-pdf", color: "text-red-500" };
  if (m.includes("word") || m.includes(".doc")) return { icon: "fa-solid fa-file-word", color: "text-blue-600" };
  if (m.includes("excel") || m.includes("sheet") || m.includes(".xls")) return { icon: "fa-solid fa-file-excel", color: "text-emerald-600" };
  if (m.includes("powerpoint") || m.includes("presentation") || m.includes(".ppt")) return { icon: "fa-solid fa-file-powerpoint", color: "text-orange-500" };
  if (m.includes("image") || m.includes("png") || m.includes("jpg") || m.includes("jpeg") || m.includes("webp")) return { icon: "fa-solid fa-file-image", color: "text-purple-500" };
  if (m.includes("text") || m.includes(".txt")) return { icon: "fa-solid fa-file-lines", color: "text-slate-500" };
  return { icon: "fa-solid fa-file", color: "text-slate-400" };
}

function formatBytes(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
}

const BLANK_FORM = {
  doc_name: "",
  notes: "",
  file: null,
};

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────
export default function DocumentsTab() {
  const fileInputRef = useRef(null);

  // ── State ──────────────────────────────────
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeAction, setActiveAction] = useState({ id: null, action: null });

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);

  const [form, setForm] = useState(BLANK_FORM);
  const [dragOver, setDragOver] = useState(false);

  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // ── Debounce search ────────────────────────
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim().toLowerCase()), 350);
    return () => clearTimeout(t);
  }, [search]);

  // ── Toast helpers ──────────────────────────
  const showError = (msg) => {
    setError(msg);
    if (msg) setTimeout(() => setError(""), 4000);
  };

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(""), 3500);
  };

  // ── Fetch documents ────────────────────────
  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApiRequest({ script: "fetch-documents" });
      if (!res?.success) throw new Error(res?.message || "Failed to load documents");
      setDocuments(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("fetch-documents error:", err.message);
      showError(err.message || "Error loading documents");
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // ── Filtered list ──────────────────────────
  const filtered = useMemo(() => {
    return documents.filter((doc) => {
      const name = (doc.doc_name || doc.name || "").toLowerCase();
      const notes = (doc.notes || "").toLowerCase();
      return !debouncedSearch || name.includes(debouncedSearch) || notes.includes(debouncedSearch);
    });
  }, [documents, debouncedSearch]);

  // ── Upload form helpers ────────────────────
  const resetForm = () => {
    setForm(BLANK_FORM);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const openUploadModal = () => {
    resetForm();
    setError("");
    setShowUploadModal(true);
  };

  const closeModals = () => {
    setShowUploadModal(false);
    setShowDeleteModal(false);
    setDocToDelete(null);
    setError("");
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      showError("Unsupported file type. Please upload PDF, Word, Excel, PowerPoint, image, or text files.");
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      showError(`File is too large. Maximum allowed size is ${MAX_FILE_MB} MB.`);
      return;
    }
    setForm((prev) => ({
      ...prev,
      file,
    }));
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files?.[0]);
  };

  // ── File action helper ─────────────────────
  const handleFileAction = async (doc, action = "view") => {
    let newWindow = null;
    if (action === "view") {
      newWindow = window.open("", "_blank");
      if (newWindow) {
        newWindow.document.write("Loading document...");
      }
    }
    
    setActiveAction({ id: doc.id, action });

    try {
      const token = localStorage.getItem("admin_token") || "";
      const res = await fetch(ADMIN_PROXY_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          script: "download-document",
          api_token: token, 
          data: { id: doc.id, action } 
        })
      });
      
      if (!res.ok) throw new Error("Failed to fetch file");
      
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      
      if (action === "view") {
        if (newWindow) {
          newWindow.location.href = url;
        } else {
          // Fallback if popup blocker prevented the new window
          window.open(url, "_blank");
        }
        // Cleanup blob url after some time (e.g., 5 mins) to allow browser to render it
        setTimeout(() => URL.revokeObjectURL(url), 300000);
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = doc.file_name || doc.doc_name || "download";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      }
    } catch (e) {
      if (newWindow) newWindow.close();
      showError("Unable to open document");
    } finally {
      setActiveAction({ id: null, action: null });
    }
  };

  // ── Upload submit ──────────────────────────
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!form.file) return showError("Please select a file to upload.");
    if (!form.doc_name.trim()) return showError("Please enter a document name.");

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", form.file);
      fd.append("doc_name", form.doc_name.trim());
      fd.append("notes", form.notes.trim());

      const res = await adminApiRequest({ script: "upload-document", formData: fd });
      if (!res?.success) throw new Error(res?.message || "Upload failed");

      showSuccess("Document uploaded successfully");
      closeModals();
      resetForm();
      fetchDocuments();
    } catch (err) {
      console.error("upload-document error:", err.message);
      showError(err.message || "Error uploading document");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Delete ─────────────────────────────────
  const openDeleteModal = (doc) => {
    setDocToDelete(doc);
    setShowDeleteModal(true);
  };

  const confirmDelete = async (e) => {
    e.preventDefault();
    if (!docToDelete?.id) return showError("No document selected.");

    setSubmitting(true);
    try {
      const res = await adminApiRequest({ script: "delete-document", data: { id: docToDelete.id } });
      if (!res?.success) throw new Error(res?.message || "Delete failed");

      showSuccess("Document deleted");
      closeModals();
      fetchDocuments();
    } catch (err) {
      console.error("delete-document error:", err.message);
      showError(err.message || "Error deleting document");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ─────────────────────────────────
  return (
    <div className="h-full overflow-y-auto">
      <div className="space-y-6 p-2">

        {/* ── Global spinner overlay ── */}
        {submitting && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/20 backdrop-blur-sm">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-300 border-t-slate-900" />
          </div>
        )}

        {/* ── Success toast ── */}
        {successMsg && (
          <div className="fixed right-6 top-6 z-50 rounded-xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white shadow-lg">
            ✓ {successMsg}
          </div>
        )}

        {/* ── Error toast ── */}
        {error && (
          <div className="fixed right-6 top-20 z-50 rounded-xl bg-red-600 px-4 py-3 text-sm font-semibold text-white shadow-lg">
            ✕ {error}
          </div>
        )}

        {/* ── Header ── */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Documents</h1>
            <p className="mt-0.5 text-sm text-slate-500">Upload and manage company documents</p>
          </div>
          <button
            onClick={openUploadModal}
            className="flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow hover:bg-slate-800 transition-colors"
          >
            <i className="fas fa-upload" />
            Upload Document
          </button>
        </div>

        {/* ── Search bar ── */}
        <div className="flex flex-wrap items-end gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="min-w-[240px] flex-1">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
              Search
            </label>
            <div className="relative">
              <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or notes…"
                className="w-full rounded-lg border border-slate-300 py-2 pl-8 pr-3 text-sm focus:border-slate-500 focus:outline-none"
              />
            </div>
          </div>

          {search && (
            <button
              onClick={() => setSearch("")}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Clear
            </button>
          )}

          <div className="ml-auto text-sm font-semibold text-slate-700">
            {filtered.length} {filtered.length === 1 ? "document" : "documents"}
          </div>
        </div>

        {/* ── Documents list ── */}
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-3 p-16 text-slate-400">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-slate-500" />
              <span className="text-sm">Loading documents…</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-4 p-16 text-center text-slate-400">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100">
                <i className="fas fa-folder-open text-3xl text-slate-300" />
              </div>
              <div>
                <p className="font-semibold text-slate-500">No documents found</p>
                <p className="mt-1 text-sm">Upload your first document using the button above.</p>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filtered.map((doc) => {
                const { icon, color } = fileIcon(doc.mime_type || doc.file_type || doc.file_name || "");
                return (
                  <div
                    key={doc.id}
                    className="flex flex-col gap-3 p-4 transition hover:bg-slate-50 sm:flex-row sm:items-center sm:justify-between"
                  >
                    {/* Left: icon + info */}
                    <div className="flex min-w-0 items-start gap-4">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-100">
                        <i className={`${icon} text-xl ${color}`} />
                      </div>

                      <div className="min-w-0">
                        <p className="font-semibold text-slate-900 truncate">
                          {doc.doc_name || doc.name || "Unnamed Document"}
                        </p>

                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-400">
                          {doc.file_size && <span>{formatBytes(doc.file_size)}</span>}
                          {doc.created_at && <span>Uploaded {formatDate(doc.created_at)}</span>}
                        </div>

                        {doc.notes && (
                          <p className="mt-1.5 text-sm text-slate-500 line-clamp-1">{doc.notes}</p>
                        )}
                      </div>
                    </div>

                    {/* Right: actions */}
                    <div className="flex shrink-0 items-center gap-2 sm:ml-4">
                      <button
                        onClick={() => handleFileAction(doc, "view")}
                        disabled={activeAction.id === doc.id}
                        className="flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="View Document"
                      >
                        {activeAction.id === doc.id && activeAction.action === "view" ? (
                          <i className="fas fa-spinner fa-spin text-xs" />
                        ) : (
                          <i className="fas fa-eye text-xs" />
                        )}
                        <span>View</span>
                      </button>

                      <button
                        onClick={() => handleFileAction(doc, "download")}
                        disabled={activeAction.id === doc.id}
                        className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Download Document"
                      >
                        {activeAction.id === doc.id && activeAction.action === "download" ? (
                          <i className="fas fa-spinner fa-spin text-xs" />
                        ) : (
                          <i className="fas fa-download text-xs" />
                        )}
                        <span>Download</span>
                      </button>

                      <button
                        onClick={() => openDeleteModal(doc)}
                        className="flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-100 transition-colors"
                        title="Delete document"
                      >
                        <i className="fas fa-trash text-xs" />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Upload modal ── */}
        {showUploadModal && (
          <Modal
            title="Upload Document"
            onClose={() => { closeModals(); resetForm(); }}
            onSubmit={handleUpload}
            submitButtonText={submitting ? "Uploading…" : "Upload Document"}
            showCancel
            showSubmitButton
            className="max-w-xl"
            submitButtonClass="bg-slate-900 hover:bg-slate-800 text-white"
          >
            <div className="space-y-5">

              {/* File drop zone */}
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleFileDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`flex cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
                  dragOver
                    ? "border-indigo-400 bg-indigo-50"
                    : form.file
                    ? "border-emerald-400 bg-emerald-50"
                    : "border-slate-300 bg-slate-50 hover:border-slate-400 hover:bg-slate-100"
                }`}
              >
                {form.file ? (
                  <>
                    <i className={`${fileIcon(form.file.type).icon} text-3xl ${fileIcon(form.file.type).color}`} />
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{form.file.name}</p>
                      <p className="text-xs text-slate-500">{formatBytes(form.file.size)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setForm((p) => ({ ...p, file: null }));
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                      className="text-xs text-red-500 hover:text-red-700 underline"
                    >
                      Remove file
                    </button>
                  </>
                ) : (
                  <>
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-200">
                      <i className="fas fa-cloud-upload-alt text-2xl text-slate-500" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-700">
                        Drag & drop a file here, or <span className="text-indigo-600">browse</span>
                      </p>
                      <p className="mt-1 text-xs text-slate-400">
                        PDF, Word, Excel, PowerPoint, images, text — max {MAX_FILE_MB} MB
                      </p>
                    </div>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPT_STRING}
                  className="hidden"
                  onChange={(e) => handleFileSelect(e.target.files?.[0])}
                />
              </div>

              {/* Document name */}
              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">
                  Document Name *
                </label>
                <input
                  type="text"
                  value={form.doc_name}
                  onChange={(e) => setForm((p) => ({ ...p, doc_name: e.target.value }))}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-slate-500 focus:outline-none"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">
                  Notes <span className="font-normal text-slate-400">(optional)</span>
                </label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                  rows={3}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-slate-500 focus:outline-none resize-none"
                />
              </div>
            </div>
          </Modal>
        )}

        {/* ── Delete confirm modal ── */}
        {showDeleteModal && (
          <Modal
            title="Delete Document"
            onClose={closeModals}
            onSubmit={confirmDelete}
            submitButtonText={submitting ? "Deleting…" : "Yes, Delete"}
            showCancel
            showSubmitButton
            className="max-w-md"
            submitButtonClass="bg-red-600 hover:bg-red-700 text-white"
          >
            <div className="space-y-3 text-sm text-slate-600">
              <p>Are you sure you want to permanently delete this document? This action cannot be undone.</p>
              {docToDelete && (
                <div className="flex items-center gap-3 rounded-xl bg-slate-50 p-3">
                  <i className={`${fileIcon(docToDelete.mime_type || docToDelete.file_type || "").icon} text-xl ${fileIcon(docToDelete.mime_type || docToDelete.file_type || "").color}`} />
                  <p className="font-semibold text-slate-800">
                    {docToDelete.doc_name || docToDelete.name}
                  </p>
                </div>
              )}
            </div>
          </Modal>
        )}

      </div>
    </div>
  );
}
